import { useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, CheckCircle2, XCircle } from "lucide-react";

export default function DispatchImport() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);

  const downloadTemplate = async () => {
    const token = localStorage.getItem("srad_token");
    const url = `${API}/dispatches/import/template?auth=${token}`;
    // Use fetch with header so cookie/token flow works
    const resp = await fetch(`${API}/dispatches/import/template`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) return toast.error("Template download failed");
    const blob = await resp.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "dispatch-import-template.xlsx";
    link.click();
  };

  const doPreview = async () => {
    if (!file) return toast.error("Choose an Excel file first");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/dispatches/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    setCommitting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/dispatches/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} dispatches · Skipped ${data.skipped}`);
      setPreview(null); setFile(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  return (
    <div data-testid="page-import" className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Bulk</div>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Excel Import</h1>
        <p className="text-sm text-slate-500 mt-1">Bulk-load historical dispatches. Download the template, fill it, and upload — we preview all rows before committing.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Step 1 — Download the template</div>
            <div className="text-xs text-slate-500 mt-0.5">Excel file with the exact column headers we need.</div>
          </div>
          <button data-testid="btn-download-template" onClick={downloadTemplate} className="bg-slate-900 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2">
            <Download size={14} /> Download Template
          </button>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-sm font-semibold">Step 2 — Upload filled file</div>
          <div className="text-xs text-slate-500 mt-0.5 mb-3">Use exact <b>client_code</b> (e.g., SRAD-CL-0001) and <b>transporter_code</b> (SRAD-TR-0001) from the masters — imports with unknown codes will be skipped.</div>
          <div className="flex items-center gap-3">
            <input data-testid="import-file" type="file" accept=".xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
            <button data-testid="btn-preview-import" disabled={!file} onClick={doPreview} className="bg-[var(--navy)] text-white text-sm px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50">
              <Upload size={14} /> Preview
            </button>
          </div>
        </div>

        {preview && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            <div className="flex items-center gap-4">
              <div className="text-sm"><b>{preview.total}</b> total rows</div>
              <div className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14} /><b>{preview.valid}</b> valid</div>
              <div className="text-sm text-rose-700 flex items-center gap-1"><XCircle size={14} /><b>{preview.invalid}</b> with errors</div>
              <div className="ml-auto">
                <button
                  data-testid="btn-commit-import"
                  disabled={committing || preview.valid === 0}
                  onClick={doCommit}
                  className="bg-[var(--amber)] hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50"
                >
                  {committing ? "Importing…" : `Import ${preview.valid} valid rows`}
                </button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-md max-h-96 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 text-left">
                    <th className="px-3 py-2">Row</th><th className="px-3 py-2">Invoice</th>
                    <th className="px-3 py-2">Client</th><th className="px-3 py-2">Transporter</th>
                    <th className="px-3 py-2">LR</th><th className="px-3 py-2">Status / Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).map((r) => (
                    <tr key={r.row} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono">{r.row}</td>
                      <td className="px-3 py-1.5">{r.invoice_number}</td>
                      <td className="px-3 py-1.5 font-mono">{r.client_code}</td>
                      <td className="px-3 py-1.5 font-mono">{r.transporter_code}</td>
                      <td className="px-3 py-1.5 font-mono">{r.lr_number}</td>
                      <td className="px-3 py-1.5">
                        {r.errors?.length ? <span className="text-rose-700">{r.errors.join(" · ")}</span> : <span className="text-emerald-700">OK</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
